import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheck, Ban, FileWarning, Printer, Loader2 } from 'lucide-react';
import { T, glassLight, primaryButton, Badge } from '../ui';
import { LOGO_DATA_URI } from '../Logo';
import { fetchPedidoPorToken } from './gasData';
import { esWOP, etiquetaTipo, imprimirPedido, suplidorDe } from './pedidoDocumento';
import type { Pedido } from '../types';

// Etiqueta legible de cada producto, duplicada a propósito en forma mínima:
// esta pantalla es pública y no debe arrastrar la lógica del módulo interno.
const PRODUCTO_LABEL: Record<string, string> = {
  jetA1: 'Jet A-1 / Kerosen',
  gasolinaRegular: 'Gasolina Regular',
  gasolinaPremium: 'Gasolina Premium',
  gasoilRegular: 'Gasoil Regular',
  gasoilOptimo: 'Gasoil Óptimo',
  fuelOilReg: 'Fuel Oil Reg.',
  fuelOilPrem: 'Fuel Oil Prem.',
  glp: 'GLP'
};
const EMPRESA_NOMBRE = 'Junquito Gas, S.R.L.';
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const fmtFecha = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};
function Marco({
  children
}: {
  children: React.ReactNode;
}) {
  return <div style={{
    minHeight: '100vh',
    background: '#FFFCF8',
    fontFamily: "'DM Sans',-apple-system,sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px'
  }} data-id="element-1280" data-mp="qzv9tbtj48hd">

      <img src={LOGO_DATA_URI} alt="Junquito Gas" style={{
      height: 72,
      marginBottom: 26
    }} data-id="element-1281" data-mp="10tlrnxv5hko3" />

      <div style={{
      width: '100%',
      maxWidth: 720
    }} data-id="element-1282" data-mp="19rxrmqhwput7">

        {children}
      </div>
    </div>;
}
function Dato({
  label,
  valor
}: {
  label: string;
  valor: string;
}) {
  return <div data-id="element-1283" data-mp="3bjalmkic6iah">
      <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 3
    }} data-id="element-1284" data-mp="3bjkd6cj14xia">

        {label}
      </div>
      <div style={{
      fontSize: 14,
      color: T.text,
      fontWeight: 600
    }} data-id="element-1285" data-mp="1keqypghih7up">

        {valor || '—'}
      </div>
    </div>;
}

// Pantalla pública: se muestra ANTES del login cuando la URL trae
// ?verificar=<token>. Es la copia digital a la que lleva el QR impreso en el
// formulario, para que quien recibe el papel pueda cotejarlo contra el
// sistema sin tener usuario.
export function VerificarPedido({
  token
}: {
  token: string;
}) {
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'error'>('cargando');
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const res = await fetchPedidoPorToken(token);
      if (cancelado) return;
      if (res.error) {
        setError(res.error);
        setEstado('error');
        return;
      }
      // Un borrador no es un documento emitido: aunque alguien conociera el
      // token, no debe poder verlo hasta que esté autorizado.
      if (!res.pedido || res.pedido.estado === 'borrador') {
        setPedido(null);
        setEstado('listo');
        return;
      }
      setPedido(res.pedido);
      setEstado('listo');
    })();
    return () => {
      cancelado = true;
    };
  }, [token]);
  if (estado === 'cargando') {
    return <Marco data-id="element-1286" data-mp="3f1551753ih37">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center',
        color: T.textSecondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12
      }} data-id="element-1287" data-mp="39zxyfbeovny4">

          <Loader2 size={26} style={{
          color: T.accent,
          animation: 'spin 1s linear infinite'
        }} data-id="element-1288" data-mp="316l0fv1rpw56" />

          <style data-id="element-1289" data-mp="1lflav9w5i7n">{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          Verificando documento…
        </div>
      </Marco>;
  }
  if (estado === 'error' || !pedido) {
    return <Marco data-id="element-1290" data-mp="3dg0yig82nz65">
        <div style={{
        ...glassLight,
        padding: 40,
        textAlign: 'center'
      }} data-id="element-1291" data-mp="3dfhcanbe2omb">

          <FileWarning size={34} style={{
          color: T.danger,
          marginBottom: 14
        }} data-id="element-1292" data-mp="3ih6nq42ix4ax" />

          <div style={{
          fontSize: 19,
          fontWeight: 800,
          color: T.text,
          marginBottom: 8
        }} data-id="element-1293" data-mp="34igtcw739iq9">

            Documento no encontrado
          </div>
          <div style={{
          fontSize: 14,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-1294" data-mp="3z4stj3t4kk">

            {error ?? 'Este código no corresponde a ninguna orden de pedido emitida. ' + 'Si lo escaneaste de un documento impreso, verifícalo con la empresa ' + 'antes de despachar.'}
          </div>
        </div>
      </Marco>;
  }
  const anulado = pedido.estado === 'anulado';
  const total = pedido.lineas.reduce((s, l) => s + (l.galones || 0), 0);
  return <Marco data-id="element-1295" data-mp="ns9xh9enbxxr">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1296" data-mp="nsrsk2pko643">

        {/* Veredicto arriba del todo: es lo único que importa de un vistazo */}
        <div style={{
        ...glassLight,
        padding: '22px 24px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        borderColor: anulado ? `${T.danger}44` : `${T.ok}44`,
        background: anulado ? T.dangerSoft : T.okSoft
      }} data-id="element-1297" data-mp="23iixqx8gwz0g">

          {anulado ? <Ban size={30} style={{
          color: T.danger,
          flexShrink: 0
        }} data-id="element-1298" data-mp="1pl45zluqsf5b" /> : <BadgeCheck size={30} style={{
          color: T.ok,
          flexShrink: 0
        }} data-id="element-1299" data-mp="1klrgsugeevzk" />}
          <div data-id="element-1300" data-mp="1ziihr85q0fra">
            <div style={{
            fontSize: 18,
            fontWeight: 800,
            color: anulado ? T.danger : T.ok
          }} data-id="element-1301" data-mp="1zeus4k3ecdhu">

              {anulado ? 'Orden anulada' : 'Documento auténtico'}
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 2
          }} data-id="element-1302" data-mp="35xsxgjp768v4">

              {anulado ? 'Esta orden fue anulada. No debe despacharse contra este documento.' : `Autorizada por ${pedido.autorizadoPor || '—'} el ${fmtFecha(pedido.autorizadoFecha)}.`}
            </div>
          </div>
        </div>

        <div style={{
        ...glassLight,
        padding: 24
      }} data-id="element-1303" data-mp="3mwkohpfxknm7">

          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 20
        }} data-id="element-1304" data-mp="3rx8dk5klmuds">

            <span style={{
            fontSize: 21,
            fontWeight: 800,
            color: T.text
          }} data-id="element-1305" data-mp="2z89vf8gh7s8h">

              Orden {pedido.numeroOrden}
            </span>
            <Badge label={etiquetaTipo(pedido.tipo)} color={esWOP(pedido.tipo) ? T.textMuted : T.brand} soft={esWOP(pedido.tipo) ? T.surfaceSubtle : T.accentSoft} dot={false} data-id="element-1306" data-mp="38ymesxck1fd1" />

          </div>

          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))',
          gap: 18,
          marginBottom: 24
        }} data-id="element-1307" data-mp="2gibmkq4utv3s">

            <Dato label="Fecha" valor={fmtFecha(pedido.fecha)} data-id="element-1308" data-mp="xl4icm85gmbx" />
            <Dato label="Suplidor" valor={suplidorDe(pedido.tipo).razonSocial} data-id="element-1309" data-mp="tn6o34ziva1o" />
            <Dato label="Conductor" valor={pedido.conductor} data-id="element-1310" data-mp="olprhlo2m9qw" />
            <Dato label="Pase del chofer" valor={pedido.paseChofer} data-id="element-1311" data-mp="jno8f0qnaaff" />
            <Dato label="Ficha de la cola" valor={pedido.fichaCola} data-id="element-1312" data-mp="jnfy9glwpj7x" />
            <Dato label="Placa del camión" valor={pedido.placaCamion} data-id="element-1313" data-mp="epmokdlea72w" />
            <Dato label="Total" valor={`${fmtGal(total)} galones`} data-id="element-1314" data-mp="9xct6i1iget8" />
          </div>

          <div style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: T.textMuted,
          marginBottom: 10
        }} data-id="element-1315" data-mp="5waty17lg2r9">

            Detalle del despacho
          </div>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 24
        }} data-id="element-1316" data-mp="3s2ptc4rdpo67">

            {pedido.lineas.map((l, i) => <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            alignItems: 'baseline',
            flexWrap: 'wrap',
            padding: '10px 14px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd
          }} data-id="element-1317" data-mp="1fu946lif9kqu" data-mpi={i}>

                <div data-id="element-1318" data-mp="w58gf0na7h12">
                  <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: T.text
              }} data-id="element-1319" data-mp="w4yr7fa29dwf">

                    {l.destino}
                  </div>
                  <div style={{
                fontSize: 12,
                color: T.textMuted
              }} data-id="element-1320" data-mp="n5bpesdf662u">

                    {PRODUCTO_LABEL[l.producto] ?? l.producto}
                    {esWOP(pedido.tipo) ? ` · Compart. ${l.compartimiento}` : ''}
                    {l.rncDestino ? ` · RNC ${l.rncDestino}` : ''}
                  </div>
                </div>
                <div style={{
              fontSize: 15,
              fontWeight: 800,
              color: T.accent,
              whiteSpace: 'nowrap'
            }} data-id="element-1321" data-mp="3up1vjbtjqaju">

                  {fmtGal(l.galones)} gal
                </div>
              </div>)}
          </div>

          <button type="button" onClick={() => imprimirPedido(pedido)} style={{
          ...primaryButton,
          width: '100%',
          justifyContent: 'center'
        }} data-id="element-1322" data-mp="8j4ty9c2nbex">

            <Printer size={15} data-id="element-1323" data-mp="hgyq9k9u6523" /> Ver / descargar el formulario
          </button>
        </div>

        <div style={{
        textAlign: 'center',
        fontSize: 11,
        color: T.textMuted,
        marginTop: 18,
        lineHeight: 1.6
      }} data-id="element-1324" data-mp="dg500k8x8e61">

          Código de verificación{' '}
          <span style={{
          fontFamily: 'monospace'
        }} data-id="element-1325" data-mp="majewlpjhmom">
            {pedido.token.slice(0, 12)}
          </span>
          <br data-id="element-1326" data-mp="114em7na1r3xp" />
          Documento emitido por {EMPRESA_NOMBRE}.
        </div>
      </motion.div>
    </Marco>;
}